// SPDX-License-Identifier: MIT
import { supabase } from '../auth/supabase';

// Mirrors the edge fn's DiscoveryResult shape (supabase/functions/
// discover-attachments/lib.ts). The questions array is the load-bearing
// piece — each has a short id (q1, q2, ...) the UI uses as form-field
// key when posting answers back to the magnify call.
export interface DiscoveryQuestion {
  id: string;
  question: string;
  placeholder?: string;
  source?: string; // per-question provenance from the producer (optional)
  why?: string;    // file-grounded reason from the producer (optional)
}

export interface DiscoveryResult {
  discovery_summary: string;
  questions: DiscoveryQuestion[];
  attachments_seen: string[];
  attachments_skipped: { name: string; reason: string }[];
}

// Run the multimodal discovery pass for a saved project. The edge fn
// reads the project + its storage attachments (RLS-scoped) and asks
// Claude to summarize + ask 3-5 focused questions. Errors propagate
// as Error with .status attached so the caller can branch.
export async function discoverAttachments(
  projectId: string,
): Promise<DiscoveryResult> {
  const { data, error } = await supabase.functions.invoke<DiscoveryResult>(
    'discover-attachments',
    { body: { project_id: projectId } },
  );
  if (error) {
    const ctx = (error as { context?: Response }).context;
    const status = ctx?.status;
    const wrapped = new Error(error.message) as Error & { status?: number };
    if (typeof status === 'number') wrapped.status = status;
    throw wrapped;
  }
  if (!data) throw new Error('Empty response from discover-attachments');
  return data;
}

// Build the supplementary context string passed to magnify's
// attachments_context field after the discovery+Q&A round. Format is
// designed to be obvious to Claude : a clearly-labelled DISCOVERY
// section + a transcript-style Q&A list. Combined with the existing
// extracted-text context (notes_aurum.md, etc.), magnify receives the
// full conversational history when it regenerates.
export function buildRefinementContext(
  baseTextContext: string,
  discovery: DiscoveryResult,
  answers: Record<string, string>,
): string {
  const lines: string[] = [];
  if (baseTextContext.trim().length > 0) {
    lines.push(baseTextContext.trim());
    lines.push('');
  }
  lines.push('--- DISCOVERY (Genesis a regardé tes fichiers) ---');
  lines.push(discovery.discovery_summary);
  lines.push('');
  lines.push('--- QUESTIONS & RÉPONSES ---');
  for (const q of discovery.questions) {
    const a = (answers[q.id] ?? '').trim();
    lines.push(`Q: ${q.question}`);
    lines.push(`R: ${a || '(pas de réponse — utilise ton jugement)'}`);
    lines.push('');
  }
  lines.push('--- INSTRUCTIONS ---');
  lines.push(
    'Utilise ces réponses pour produire un manifest CALÉ sur ce que veut vraiment l\'utilisateur. Les réponses priment sur tes hypothèses initiales. Si une réponse est absente, prends la décision la plus pragmatique en motivant brièvement dans la stack ou les first_steps.',
  );
  return lines.join('\n');
}
